import { useState } from "react"
import { Link } from "react-router"

import { useGhlAnalytics } from "@/lib/api/use-ghl"
import type { GhlAnalytics } from "@/lib/api/ghl-types"
import { cx } from "@/lib/utils/cx"
import { SectionHead, hours, pct, pill, usd, usdK } from "./shared"

// Analytics — the numbers, all derived from stored message, email, pipeline,
// appointment and task events. No metric here is invented: each one names
// what it counts. Light, like the rest of the desk.

const PERIODS = [7, 30, 90] as const

export function Analytics({ teamId }: { teamId: number | string }) {
  const [days, setDays] = useState<(typeof PERIODS)[number]>(30)
  const { data: a, isLoading } = useGhlAnalytics(teamId, days)

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-zinc-500">Last {days} days · derived from your stored message, email, pipeline and appointment events.</p>
        <div role="radiogroup" aria-label="Period" className="flex gap-1.5">
          {PERIODS.map((p) => (
            <button key={p} type="button" role="radio" aria-checked={days === p} onClick={() => setDays(p)} className={pill(days === p)}>
              {p} days
            </button>
          ))}
        </div>
      </div>

      {isLoading || !a ? (
        <div className="h-[132px] animate-pulse rounded-xl border border-zinc-200 bg-white" />
      ) : (
        <Tiles a={a} />
      )}

      {a && (
        <div className="mt-8 grid gap-x-10 gap-y-8 lg:grid-cols-2">
          <section>
            <SectionHead title="Messages by day" meta={`${a.messages.sms + a.messages.emails} sent · ${a.messages.inbound} received`} />
            <Timeline rows={a.timeline} />
          </section>

          <section>
            <SectionHead title="Email engagement" meta={`${a.email.sent} sent`} />
            <Funnel
              rows={[
                { label: "Delivered", n: a.email.delivered, of: a.email.sent },
                { label: "Opened", n: a.email.opened, of: a.email.delivered },
                { label: "Clicked", n: a.email.clicked, of: a.email.delivered },
                { label: "Bounced", n: a.email.bounced, of: a.email.sent, bad: true },
              ]}
            />
          </section>

          <section>
            <SectionHead title={a.pipeline.name ?? "Pipeline"} meta={`${a.pipeline.open.count} open · ${usdK(a.pipeline.open.value)}`} action={<Link to="/marketing/pipeline" className="text-[12.5px] font-semibold text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900">Open the board</Link>} />
            <Stages a={a} />
          </section>

          <section>
            <SectionHead title="Texts & calls" />
            <dl className="divide-y divide-zinc-100">
              <Row k="Texts sent" v={String(a.messages.sms)} sub={`${pct(a.messages.smsDeliveryRate)} delivered · ${a.messages.smsFailed} failed`} />
              <Row k="Calls" v={String(a.messages.calls)} />
              <Row k="Contacts who replied" v={`${a.replies.contactsReplied} of ${a.replies.contactsMessaged}`} sub={`${pct(a.replies.replyRate)} reply rate`} />
              <Row k="Median time to answer a reply" v={hours(a.replies.medianResponseHours)} />
            </dl>
          </section>

          <section>
            <SectionHead title="Appointments" meta={`${a.appointments.booked} booked`} />
            <dl className="divide-y divide-zinc-100">
              <Row k="Showed" v={String(a.appointments.showed)} />
              <Row k="No-show" v={String(a.appointments.noshow)} bad={a.appointments.noshow > 0} />
              <Row k="Upcoming" v={String(a.appointments.upcoming)} />
            </dl>
          </section>

          <section>
            <SectionHead title="Tasks & contacts" action={<Link to="/marketing/tasks" className="text-[12.5px] font-semibold text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900">See tasks</Link>} />
            <dl className="divide-y divide-zinc-100">
              <Row k="Open tasks" v={String(a.tasks.open)} sub={a.tasks.overdue ? `${a.tasks.overdue} overdue` : "none overdue"} bad={a.tasks.overdue > 0} />
              <Row k="Completed" v={String(a.tasks.completed)} />
              <Row k="Contacts" v={String(a.contacts.total)} sub={`${a.contacts.added} added · ${a.contacts.optedOut} opted out`} />
            </dl>
            {a.contacts.topTags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {a.contacts.topTags.map((t) => (
                  <Link key={t.tag} to={`/marketing/contacts?tag=${encodeURIComponent(t.tag)}`} className="inline-flex h-7 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 text-[12px] text-zinc-700 hover:bg-zinc-50">
                    {t.tag} <span className="tabular-nums text-zinc-400">{t.count}</span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  )
}

function Tiles({ a }: { a: GhlAnalytics }) {
  const items: Array<{ label: string; value: string; sub?: string; money?: boolean }> = [
    { label: "Messages sent", value: String(a.messages.sms + a.messages.emails), sub: `${a.messages.sms} texts · ${a.messages.emails} emails · ${a.messages.calls} calls` },
    { label: "Texts delivered", value: pct(a.messages.smsDeliveryRate), sub: `${a.messages.smsFailed} failed` },
    { label: "Emails opened", value: pct(a.email.openRate), sub: `${a.email.opened} of ${a.email.delivered} delivered` },
    { label: "Reply rate", value: pct(a.replies.replyRate), sub: `${a.replies.contactsReplied} of ${a.replies.contactsMessaged} contacts` },
    { label: "Answered in", value: hours(a.replies.medianResponseHours), sub: "median, first reply" },
    { label: "Booked", value: String(a.appointments.booked), sub: `${a.appointments.showed} showed · ${a.appointments.noshow} no-show` },
    { label: "Open pipeline", value: usdK(a.pipeline.open.value), sub: `${a.pipeline.open.count} open deals`, money: true },
    { label: "Won", value: usdK(a.pipeline.won.value), sub: `${a.pipeline.won.count} won · ${a.pipeline.lost.count} lost`, money: true },
  ]
  return (
    <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-zinc-200 bg-zinc-200 md:grid-cols-4">
      {items.map((it) => (
        <div key={it.label} className="min-w-0 bg-white px-4 py-4">
          <dt className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">{it.label}</dt>
          <dd className={cx("mt-1.5 text-[26px] font-semibold tabular-nums leading-none tracking-tight", it.money ? "text-emerald-700" : "text-zinc-900")}>{it.value}</dd>
          {it.sub && <dd className="mt-1.5 truncate text-[12px] text-zinc-500">{it.sub}</dd>}
        </div>
      ))}
    </dl>
  )
}

function Row({ k, v, sub, bad }: { k: string; v: string; sub?: string; bad?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2.5">
      <dt className="text-[13.5px] text-zinc-700">{k}</dt>
      <dd className="text-right">
        <span className={cx("text-[15px] font-semibold tabular-nums", bad ? "text-red-700" : "text-zinc-900")}>{v}</span>
        {sub && <span className="ml-2 text-[12px] text-zinc-500">{sub}</span>}
      </dd>
    </div>
  )
}

function Funnel({ rows }: { rows: Array<{ label: string; n: number; of: number; bad?: boolean }> }) {
  return (
    <dl className="divide-y divide-zinc-100">
      {rows.map((r) => {
        const share = r.of > 0 ? r.n / r.of : 0
        return (
          <div key={r.label} className="py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[13.5px] text-zinc-700">{r.label}</dt>
              <dd className="text-[15px] font-semibold tabular-nums text-zinc-900">
                {r.n} <span className="ml-1 text-[12px] font-normal text-zinc-500">{r.of ? pct(share) : ""}</span>
              </dd>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-zinc-100">
              <div className={cx("h-full rounded-full", r.bad ? "bg-red-400" : "bg-zinc-900")} style={{ width: `${Math.min(100, share * 100)}%` }} />
            </div>
          </div>
        )
      })}
    </dl>
  )
}

function Stages({ a }: { a: GhlAnalytics }) {
  const max = Math.max(1, ...a.pipeline.stages.map((s) => s.value))
  return (
    <dl className="divide-y divide-zinc-100">
      {a.pipeline.stages.map((s) => (
        <div key={s.id} className="py-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[13.5px] text-zinc-700">{s.name}</dt>
            <dd className="text-[15px] font-semibold tabular-nums text-zinc-900">
              {usdK(s.value)} <span className="ml-1 text-[12px] font-normal text-zinc-500">{s.count} {s.count === 1 ? "deal" : "deals"}</span>
            </dd>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-zinc-100">
            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(s.value / max) * 100}%` }} />
          </div>
        </div>
      ))}
      <div className="flex items-baseline justify-between gap-3 py-2.5">
        <dt className="text-[13.5px] text-zinc-700">Won · lost</dt>
        <dd className="text-[15px] font-semibold tabular-nums text-zinc-900">
          <span className="text-emerald-700">{usd(a.pipeline.won.value)}</span>
          <span className="mx-1.5 text-zinc-300">·</span>
          <span className="text-zinc-500">{usd(a.pipeline.lost.value)}</span>
        </dd>
      </div>
    </dl>
  )
}

function Timeline({ rows }: { rows: GhlAnalytics["timeline"] }) {
  const max = Math.max(1, ...rows.map((r) => r.sms + r.email + r.calls + r.inbound))
  const wide = rows.length > 31
  return (
    <div>
      <div className="flex h-36 items-end gap-px" role="img" aria-label="Messages per day">
        {rows.map((r) => {
          const total = r.sms + r.email + r.calls + r.inbound
          return (
            <div key={r.date} className="flex h-full min-w-0 flex-1 flex-col-reverse" title={`${r.date}: ${r.sms} texts, ${r.email} emails, ${r.calls} calls, ${r.inbound} received`}>
              <div style={{ height: `${(r.sms / max) * 100}%` }} className="w-full bg-zinc-800" />
              <div style={{ height: `${(r.email / max) * 100}%` }} className="w-full bg-sky-500" />
              <div style={{ height: `${(r.calls / max) * 100}%` }} className="w-full bg-amber-400" />
              <div style={{ height: `${(r.inbound / max) * 100}%` }} className="w-full bg-emerald-500" />
              {total === 0 && <div className="h-px w-full bg-zinc-200" />}
            </div>
          )
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-zinc-400">
        <span>{rows[0]?.date.slice(5)}</span>
        {!wide && <span>{rows[Math.floor(rows.length / 2)]?.date.slice(5)}</span>}
        <span>today</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-zinc-600">
        <Legend cls="bg-zinc-800" label="Texts" /><Legend cls="bg-sky-500" label="Emails" /><Legend cls="bg-amber-400" label="Calls" /><Legend cls="bg-emerald-500" label="Received" />
      </div>
    </div>
  )
}
function Legend({ cls, label }: { cls: string; label: string }) {
  return <span className="inline-flex items-center gap-1.5"><span className={cx("size-2 rounded-[2px]", cls)} aria-hidden />{label}</span>
}
